// The KPI DETAIL view (KPI Screen v2, the "Signal" re-layout): a back bar, the headline row (name +
// definition + latest figure + the stated target gap), then two columns — the trend chart with the
// closed-loop "What moved it" Task list on the left, and the provenance/decision rail on the right (the
// Value EVIDENCE on demand, G-011; the confirm/activate gate; the housekeeping facts — split into
// MetricDetailSections.tsx). It renders one Metric's projection: lifecycle state (proposed / active /
// archived), the latest Value with its trend DIRECTION only (G-106, an arrow, never a raw slope), the
// in-flight Tasks, and nothing the projection does not carry (P-SELF). Activation / value-approval are
// Fence-crossing Gates owned by `metrics` (E): the view ROUTES the authenticated human intent and the
// parent re-reads the recorded state — it never writes an activation/approval field (P-GATE, AC-6).
// Tokens only (G-181); no internal id rendered (G-197).

import { useState } from 'react';
import type {
  ApprovalGateKind,
  MetricCard,
  MetricEvidenceSheet,
  MetricLinkedTask,
  MetricPoint,
} from '../projections.ts';
import type { RouteResult } from '../api-client.ts';
import { Icon } from '../../app/Icon.tsx';
import { Btn, StatusPill, TrendArrow } from '../primitives.tsx';
import { MetricChart, type ChartMarker } from './MetricChart.tsx';
import { MetricProposalGate } from './MetricProposalGate.tsx';
import { DetailTasks, DetailEvidence, DetailHousekeeping } from './MetricDetailSections.tsx';
import { useMetricDetailActions } from './useMetricDetailActions.ts';
import {
  fmtMetricValue,
  fmtMetricDelta,
  fmtTargetGap,
  windowedSeries,
  type ChartRange,
} from './metric-format.ts';

export interface MetricDetailProps {
  readonly metric: MetricCard;
  /** Return to the overview grid (client view-state; the projection stays whole). */
  readonly onBack: () => void;
  /** Load the latest Value's evidence sheet on demand (G-011). */
  readonly loadEvidence: (valueId: string) => Promise<MetricEvidenceSheet>;
  /** Route an activate / confirm-value intent to the owning `metrics` Gate. */
  readonly onExercise: (kind: ApprovalGateKind, objectId: string) => Promise<RouteResult>;
  /** Permanently discard this unactivated proposal. */
  readonly onDelete: (metricId: string) => Promise<RouteResult>;
  /** Route a collect-now intent for this ACTIVE Metric to the owning `metrics` collection path. */
  readonly onCollectNow: (metricId: string) => Promise<RouteResult>;
}

/**
 * The collect-now card in the decision rail (BUILD-DEC-150). Only an ACTIVE Metric offers it — the
 * mechanism refuses any other lifecycle state, so no dead control is rendered (the honest-affordance
 * posture); there is no client-side authority pre-check (G-180), the OWNING module decides and the rail
 * renders its sanitised refusal note. The copy states the gate plainly: a collection produces a PROPOSED
 * figure and NEVER an authoritative one (AC-E-3.3) — clicking collects, confirming records.
 */
function DetailCollectNow({
  metric,
  busy,
  onCollectNow,
}: {
  readonly metric: MetricCard;
  readonly busy: boolean;
  readonly onCollectNow: () => void;
}): React.JSX.Element | null {
  if (metric.state !== 'active') return null;
  return (
    <div className="kd-block">
      <h3 className="h-section">Collect it now</h3>
      <p className="kd-block-note">
        Runs this KPI&rsquo;s collector for the most recent completed period, rather than waiting
        for its next scheduled run. The figure comes back proposed for you to check — it only
        becomes the recorded number once you confirm it.
      </p>
      <Btn
        kind="ghost"
        sm
        disabled={busy}
        ariaLabel={`Collect now: ${metric.name}`}
        onClick={onCollectNow}
      >
        Collect now
      </Btn>
    </div>
  );
}

/** The chart legend (prototype .chart-legend): value line, target when set, task rings when pinned. */
function TrendLegend({
  target,
  markerCount,
}: {
  readonly target: number | null;
  readonly markerCount: number;
}): React.JSX.Element {
  return (
    <div className="chart-legend">
      <span className="lg">
        <span className="lg-line val" /> Value
      </span>
      {target !== null ? (
        <span className="lg">
          <span className="lg-line tgt" /> Target
        </span>
      ) : null}
      {markerCount > 0 ? (
        <span className="lg">
          <span className="lg-ring" /> Task ({markerCount})
        </span>
      ) : null}
    </div>
  );
}

/** Pin each linked Task to the charted week its last recorded activity landed in (real dates only —
 *  a Task whose activity falls outside the window gets no letter, it is simply listed). */
function chartMarkers(
  windowed: readonly MetricPoint[],
  linkedTasks: readonly MetricLinkedTask[],
): {
  readonly markers: readonly ChartMarker[];
  readonly letterByTaskId: ReadonlyMap<string, string>;
} {
  const pinned = linkedTasks
    .map((task) => {
      const day = task.updatedAt.slice(0, 10);
      const index = windowed.findIndex(
        (point) => point.periodStart.slice(0, 10) <= day && day <= point.periodEnd.slice(0, 10),
      );
      return { task, index };
    })
    .filter((entry) => entry.index >= 0)
    .sort((a, b) => (a.task.updatedAt < b.task.updatedAt ? -1 : 1));
  const markers: ChartMarker[] = [];
  const letterByTaskId = new Map<string, string>();
  pinned.forEach((entry, position) => {
    const letter = String.fromCharCode(65 + position);
    markers.push({ index: entry.index, letter });
    letterByTaskId.set(entry.task.id, letter);
  });
  return { markers, letterByTaskId };
}

/** The state pill in the detail bar (derived from the lifecycle states, never hand-set). */
function StateMarker({ metric }: { readonly metric: MetricCard }): React.JSX.Element {
  if (metric.state === 'proposed') {
    if (metric.activationReadiness?.ready !== true) {
      return <StatusPill tone="attention">Needs setup</StatusPill>;
    }
    return <StatusPill tone="attention">Awaiting activation</StatusPill>;
  }
  if (metric.latestValue?.state === 'proposed') {
    return <StatusPill tone="attention">Awaiting you</StatusPill>;
  }
  if (metric.state === 'archived') {
    return <StatusPill tone="neutral">archived</StatusPill>;
  }
  return <StatusPill tone="go">active</StatusPill>;
}

/** The confirm/activate gate card in the decision rail (prototype .md-gate, Signal-stacked). */
function DetailGate(props: {
  readonly metric: MetricCard;
  readonly busy: boolean;
  readonly onRoute: (kind: ApprovalGateKind, objectId: string) => void;
  readonly onSetup: () => void;
  readonly onDelete: () => void;
  readonly confirmDelete: boolean;
  readonly onConfirmDelete: () => void;
  readonly onCancelDelete: () => void;
}): React.JSX.Element | null {
  if (props.metric.state === 'proposed') return <MetricProposalGate {...props} />;
  const value = props.metric.latestValue;
  if (props.metric.state !== 'active' || value === null || value.state !== 'proposed') return null;
  return (
    <div className="md-gate">
      <span className="g-eyebrow">Awaiting you</span>
      <span className="g-note">
        Proposed {value.value} — confirm to make it the authoritative value.
      </span>
      <span className="g-act">
        <Btn
          sm
          disabled={props.busy}
          ariaLabel={`Confirm value: ${props.metric.name}`}
          onClick={() => props.onRoute('metric_value_authoritative', value.id)}
        >
          Confirm value
        </Btn>
      </span>
    </div>
  );
}

/** The back bar: return to the grid, the cadence stamp, and the derived lifecycle pill. */
function DetailBar({
  metric,
  onBack,
}: {
  readonly metric: MetricCard;
  readonly onBack: () => void;
}): React.JSX.Element {
  return (
    <div className="kd-bar">
      <button type="button" className="kd-back" onClick={onBack} aria-label="All KPIs">
        <Icon name="chevleft" size={15} />
        All KPIs
      </button>
      {metric.cadenceLabel !== null ? (
        <>
          <span className="kd-bar-div" aria-hidden="true" />
          <span className="kd-cadence">{metric.cadenceLabel}</span>
        </>
      ) : null}
      <span className="spacer" aria-hidden="true" />
      <StateMarker metric={metric} />
    </div>
  );
}

/** The headline row: identity + definition, the latest figure + factual delta, the stated target. */
function DetailHead({ metric }: { readonly metric: MetricCard }): React.JSX.Element {
  const value = metric.latestValue;
  return (
    <div className="kd-head">
      <div className="kd-idblock">
        <h2 className="kd-name">{metric.name}</h2>
        <div className="kd-defn">{metric.definition}</div>
      </div>
      <span className="kd-head-div" aria-hidden="true" />
      <div className="kd-headline">
        <span className="kd-now">{value !== null ? fmtMetricValue(value.value) : '—'}</span>
        <span className="kd-delta">
          {fmtMetricDelta(metric.series) ?? <TrendArrow direction={metric.trendDirection} />}
        </span>
      </div>
      <div className="kd-target">
        <div className="mono-label">Target</div>
        <div className="kd-target-v">
          {metric.target !== null ? (
            <>
              {fmtMetricValue(metric.target)}{' '}
              {value !== null ? (
                <span className="gap">· {fmtTargetGap(value.value, metric.target)}</span>
              ) : null}
            </>
          ) : (
            <span className="gap">no target set</span>
          )}
        </div>
      </div>
    </div>
  );
}

/** The chart range segment (prototype .range-seg): the charted window, client view-state only. */
function RangeSegment({
  range,
  onRange,
}: {
  readonly range: ChartRange;
  readonly onRange: (range: ChartRange) => void;
}): React.JSX.Element {
  const ranges: readonly ChartRange[] = ['6w', '12w', 'all'];
  return (
    <div className="range-seg">
      {ranges.map((option) => (
        <button
          key={option}
          type="button"
          className={option === range ? 'rg on' : 'rg'}
          aria-pressed={option === range}
          data-range={option}
          onClick={() => onRange(option)}
        >
          {option.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/** The trend card (prototype .md-card): the chart over the charted window + its legend. With fewer
 *  than two recorded points there is nothing to chart — stated honestly, never interpolated (P-SELF). */
function DetailTrend({
  metric,
  windowed,
  markers,
  range,
  onRange,
}: {
  readonly metric: MetricCard;
  readonly windowed: readonly MetricPoint[];
  readonly markers: readonly ChartMarker[];
  readonly range: ChartRange;
  readonly onRange: (range: ChartRange) => void;
}): React.JSX.Element {
  const pointsLabel =
    metric.cadenceLabel !== null ? `${metric.cadenceLabel} points` : 'recorded points';
  return (
    <div className="md-card" data-metric-trend={metric.id}>
      <div className="md-card-head">
        <div>
          <h3 className="h-section">Trend &amp; what&rsquo;s moving it</h3>
          <div className="md-sub">
            {windowed.length} {pointsLabel} ·{' '}
            {markers.length > 0
              ? `${markers.length} ${markers.length === 1 ? 'task' : 'tasks'} in this window`
              : 'no tasks in this window'}
          </div>
        </div>
        <RangeSegment range={range} onRange={onRange} />
      </div>
      {windowed.length >= 2 ? (
        <>
          <MetricChart
            name={metric.name}
            series={windowed}
            target={metric.target}
            markers={markers}
          />
          <TrendLegend target={metric.target} markerCount={markers.length} />
        </>
      ) : (
        <div className="tl-empty">
          Not enough recorded values to chart yet — the trend draws from the second authoritative
          value onward.
        </div>
      )}
    </div>
  );
}

/** The right decision rail: provenance, the gate, its transient note, and the housekeeping facts. */
function DetailSide({
  metric,
  loadEvidence,
  actions,
}: {
  readonly metric: MetricCard;
  readonly loadEvidence: (valueId: string) => Promise<MetricEvidenceSheet>;
  readonly actions: ReturnType<typeof useMetricDetailActions>;
}): React.JSX.Element {
  return (
    <aside className="kd-side" aria-label={`Provenance and decisions: ${metric.name}`}>
      <DetailEvidence metric={metric} loadEvidence={loadEvidence} />
      <DetailGate
        metric={metric}
        busy={actions.busy}
        onRoute={actions.route}
        onSetup={actions.finishSetup}
        onDelete={actions.deleteProposal}
        confirmDelete={actions.confirmDelete}
        onConfirmDelete={actions.beginDelete}
        onCancelDelete={actions.cancelDelete}
      />
      <DetailCollectNow metric={metric} busy={actions.busy} onCollectNow={actions.collectNow} />
      {actions.note !== null ? (
        <p role="status" className="gate-note">
          {actions.note}
        </p>
      ) : null}
      <DetailHousekeeping metric={metric} />
    </aside>
  );
}

/** The selected Metric's full detail view (Signal): back bar, headline, chart + decision rail. */
export function MetricDetail({
  metric,
  onBack,
  loadEvidence,
  onExercise,
  onDelete,
  onCollectNow,
}: MetricDetailProps): React.JSX.Element {
  const actions = useMetricDetailActions(metric, { onExercise, onDelete, onCollectNow });
  // The charted window (prototype 6W/12W/ALL) — client view-state over the RECORDED series only.
  const [range, setRange] = useState<ChartRange>('12w');
  const windowed = windowedSeries(metric.series, range);
  const { markers, letterByTaskId } = chartMarkers(windowed, metric.linkedTasks);

  return (
    <div className="kpi-detail">
      <DetailBar metric={metric} onBack={onBack} />
      <DetailHead metric={metric} />
      <div className="kd-cols">
        <div className="kd-main">
          <DetailTrend
            metric={metric}
            windowed={windowed}
            markers={markers}
            range={range}
            onRange={setRange}
          />
          <DetailTasks
            metric={metric}
            letterByTaskId={letterByTaskId}
            onStartTask={actions.startTask}
          />
        </div>
        <DetailSide metric={metric} loadEvidence={loadEvidence} actions={actions} />
      </div>
    </div>
  );
}
